import { HttpService } from "@dna/http-request";
import jwt_decode from "jwt-decode";
import { appDomain } from "../../domain/AppDomain";

declare var window: any;

export class HttpRequestMethod {
    static get = "GET";
    static post = "POST";
    static put = "PUT";
    static delete = "DELETE";
}

class RestApiHelper {
    httpService: HttpService;

    constructor(private _baseUrl: string) {
        this.httpService = new HttpService(this._baseUrl.replace(/\/$/g, ""));
    }

    public request(method: string, path: string, data: any, settings: any): Promise<any> {
        settings = settings || {};
        settings.headers = settings.headers || {};

        const token = appDomain.IAuthenticationService.getToken();

        if (token) {
            settings.headers["Authorization"] = `Bearer ${token}`;
        }

        if (method.toUpperCase() === "GET" && data) {
            const c = encodeURIComponent;
            const cv = (v: any) => v && v.toISOString ? v.toISOString() : v;
            if (Object.keys(data).length > 0) {
                path += "?" + Object.keys(data).map(k => `${c(k)}=${c(cv(data[k]))}`).join("&");
            }
        } else {
            if (!settings.dataType) {
                settings.dataType = "application/json";
            }
            if (settings.dataType === "application/json") {
                settings.headers["Content-Type"] = "application/json";
                data = JSON.stringify(data);
            }
        }

        return Promise.resolve(this.httpService.request(method, path, data, settings, true, false))
            .then((request: any) => {
                const xhr = request.rawRequest || request;
                if (xhr.status >= 400) {
                    const err = new Error();
                    (err as any).xhr = request.rawRequest;
                    appDomain.IHandleExceptions.handleException(err);
                    return Promise.reject(err);
                }
                return request;
            })
            .catch((err: any) => {
                appDomain.IHandleExceptions.handleException(err);
                return Promise.reject(err);
            });
    }
    public get(path: string, data?: any, settings?: any): Promise<any> {
        return this.request(HttpRequestMethod.get, path, data, settings);
    }

    public getData(path: string, data?: any, settings?: any): Promise<any> {
        return this.request(HttpRequestMethod.get, path, data, settings)
            .then((request) => request.body);
    }

    public post = (path: string, data?: any, settings?: any): Promise<any> => {
        return this.request(HttpRequestMethod.post, path, data, settings);
    }

    public put = (path: string, data?: any, settings?: any): Promise<any> => {
        return this.request(HttpRequestMethod.put, path, data, settings);
    }

    public delete(path: string, data?: any, settings?: any): Promise<any> {
        return this.request(HttpRequestMethod.delete, path, data, settings);
    }


    public downloadFile(path: string, fileName: string, data: any, settings: any): Promise<any> {
        settings.headers = settings.headers ? settings.headers : [];
        settings.headers["Content-Type"] = "application/octet-binary";
        settings.headers["Response-Type"] = "application/octet-binary";
        settings.dataType = "application/octet-binary";
        settings.responseType = "arraybuffer";

        return this.request("GET", path, data, settings).then(result => {
            if (!fileName) {
                // Get filename from headers
                fileName = "unknown.txt";
                const contentDisposition = result.headers["content-disposition"];
                if (contentDisposition) {
                    const regEx = /filename=['"]?([^;,'"]+)['"]?/gi;
                    const match = regEx.exec(contentDisposition);

                    if (match && match[1]) {
                        fileName = match[1];
                    }
                }
            }
            let blob;
            try {
                blob = new Blob([result.body], {
                    type: result.headers["content-type"] || "application/octet-binary"
                });
            } catch (e) {
                // The BlobBuilder API has been deprecated in favour of Blob, but older
                // browsers don't know about the Blob constructor
                // IE10 also supports BlobBuilder, but since the `Blob` constructor
                //  also works, there's no need to add `MSBlobBuilder`.
                let bb;
                if ((window as any).WebKitBlobBuilder) {
                    bb = new ((window as any).WebKitBlobBuilder)();
                } else {
                    bb = new ((window as any).MozBlobBuilder)();
                }

                bb.append(result.body);
                blob = bb.getBlob("application/octet-binary"); // <-- Here's the Blob
            }

            // For IE
            if (window.navigator.msSaveOrOpenBlob) {
                window.navigator.msSaveOrOpenBlob(blob, fileName);
            } else {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                document.body.appendChild(a);
                a.href = url;
                a.download = fileName;
                a.target = "_self";
                a.click();
                document.body.removeChild(a);
            }

            return true;
        });
    }
}

export let restApiHelper = new RestApiHelper(window.config.WebApiUrl);

export let restApiHelperWithoutBaseUrl = new RestApiHelper("");

export let decodeJsonWebToken = (rawToken: string) => {
    return jwt_decode(rawToken);
};
